import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { getOfferPlan, setOfferPlanCell, setOfferPlanLineOrder, addOfferPlanLine, renameOfferPlanLine, deleteOfferPlanLine, setOfferPlanLinesOrder } from '../../api/salesOfferPlan';
import { Cell } from '../../components/InlineCells';
import { OfferIcon, iconOf } from './offerIcons';
import Tabs from '../../components/Tabs';
import OffersPage from './OffersPage';
import { libelleUnite, sansInducteur } from './offerUnits';
import './OfferPlanPage.css';

/* ── Trajectoire : le plan pluriannuel PAR OFFRE (2026-09-04) ───────────────
   Une ligne par offre, groupée par compte de CA ; trois groupes de colonnes
   sur cinq années : le CA (calculé au serveur avec l'équation de l'offre), le
   volume (saisi) et le prix unitaire (saisi ; vide = repris de l'année
   précédente, la première année du prix cible). Deux réglages de lecture
   (2026-09-07) : la VUE, détail (offres et lignes, qui se saisissent) ou
   synthèse (les totaux seuls) ; le GROUPEMENT, par compte de CA ou par offre.
   Lignes sous une offre (2026-09-07) : clients, segments ou tarifs, chacune
   avec son volume et son prix ; l'offre devient alors la somme de ses lignes
   et son prix la moyenne pondérée, en lecture seule. */

const libelleVolume = (p, r) => ({
  volume_price: libelleUnite(p, r.unit), time_rate: r.unit === 'jh' ? p('jours') : p('heures'),
  capacity_occupancy: p('% occupation'), base_arpu: p('base'), base_rate: '—', project: p('valeur'),
}[r.equation] || '');
const libellePrix = (p, r) => ({ capacity_occupancy: p('par mois'), base_arpu: p('par mois') }[r.equation] || '');
/* Volumes : un chiffre après la virgule sous 100 (12.5 heures), entier au-delà. */
const round2 = (v) => Math.round(v * 100) / 100;
const vol = (r, v) => (v == null ? '' : formatNumber(v, r.decimals && Math.abs(v) < 100 ? 1 : 0));

/* DEUX SECTIONS sur une page (2026-09-12) : « Mes offres » (`section` offres,
   juste après la Synthèse : le catalogue du client, onglets Mes offres ·
   Modèles) et la « Trajectoire » (le Plan pluriannuel seul, sans onglet). */
export default function OfferPlanPage({ activeTenant, year, addToast, canWrite = false, section = 'trajectoire' }) {
  const mesOffres = section === 'offres';
  /* Trois onglets (2026-09-10) : MODÈLES d'abord, ce que l'entreprise vend et
     les valeurs par défaut de chaque modèle ; OFFRES, les produits et leurs
     réglages ; puis le PLAN par offre. On règle ce qu'on vend là où on le
     planifie ; la section Offre du menu a disparu. */
  const [onglet, setOnglet] = useState(section === 'offres' ? 'offres' : 'plan');   // chaque section ouvre sur son premier onglet
  const p = useP();
  const titre = mesOffres ? p('Mes offres') : p('Trajectoire');
  const tid = activeTenant?.id;
  const [largeurLbl, setLargeurLbl] = useState(() => {
    try { const v = Number(window.localStorage.getItem(`op_lbl_w_${tid || 'none'}`)); return v >= 120 ? v : 260; } catch (e) { return 260; }
  });
  const poserLargeurLbl = (w) => {
    const v = Math.max(120, Math.round(w));
    setLargeurLbl(v);
    try { window.localStorage.setItem(`op_lbl_w_${tid || 'none'}`, String(v)); } catch (e) { /* quota */ }
  };
  const [view, setView] = useState(null);
  const [mode, setMode] = useState('detail');     // detail | synth
  const [group, setGroup] = useState('offer');    // offer (par offre, défaut) | acc (par compte)
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);   // bande de contexte, sur la ligne de l'exercice
  const [drag, setDrag] = useState(null);         // { kind: 'acc' | 'line', id, offer } saisi par sa poignée
  /* Les colonnes affichées (CA · Volume · Prix), au choix du lecteur : sur dix années, les trois
     familles ne tiennent plus à l'écran. Aucun choix = les trois ; retenu par navigateur. */
  const [cols, setColsState] = useState(() => {
    try { const v = JSON.parse(window.localStorage.getItem('op_cols') || 'null'); return new Set(Array.isArray(v) ? v : []); } catch (e) { return new Set(); }
  });
  const setCols = (next) => { setColsState(next); try { window.localStorage.setItem('op_cols', JSON.stringify(Array.from(next))); } catch (e) { /* sans mémoire, tant pis */ } };
  const exportRef = useRef(null);

  const reload = useCallback(async () => {
    if (!tid || !year) return;
    const r = await getOfferPlan(tid, year);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setView(r.data);
  }, [tid, year, addToast]);
  /* Le plan se relit à chaque ouverture de son onglet (2026-09-11) : une offre
     créée dans l'onglet Offres n'y apparaissait qu'après un rechargement de la
     page, le plan ayant été lu une fois pour toutes au montage. */
  useEffect(() => { if (onglet === 'plan') reload(); }, [reload, onglet]);

  const excel = useViewExcelExport({ ref: exportRef, sheetName: titre, fileBase: titre, year, addToast });
  const excelRun = useRef(null); excelRun.current = excel.run;
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: () => excelRun.current && excelRun.current() }], []);
  useRegisterPageExports(exportOptions);

  const ca = (v) => (v == null || Math.abs(v) < 0.005 ? '–' : (unit === 'kchf' ? formatNumber(v / 1000, 0) : formatNumber(v, 0)));
  const nb = (v, d = 0) => (v == null ? '' : formatNumber(v, d));
  const write = async (offerId, y, patch, lineId = null) => {
    const r = await setOfferPlanCell(tid, offerId, y, lineId ? { ...patch, line_id: lineId } : patch);
    if (r.error) addToast && addToast(r.error, 'error');
    await reload();
  };
  /* Toute écriture attend sa réponse AVANT de relire : sinon la relecture part en même temps
     que l'écriture et ramène l'ancienne valeur (le libellé « qu'il fallait taper deux fois »). */
  const appel = async (promesse) => { const r = await promesse; if (r.error) addToast && addToast(r.error, 'error'); await reload(); };
  const ajouterLigne = (offerId) => appel(addOfferPlanLine(tid, offerId, p('Nouvelle ligne')));
  const renommerLigne = (lineId, v) => appel(renameOfferPlanLine(tid, lineId, v));
  /* Supprimer une ligne de Trajectoire supprime aussi sa ligne de Prévisions et ses données
     (demande d'Olivier, 2026-10-12) : on le dit avant. */
  const supprimerLigne = (lineId) => {
    if (window.confirm(p('Supprimer cette ligne de la Trajectoire ? Sa ligne dans les Prévisions part avec elle, portefeuille, nouvelles ventes, coût et réalisé compris.'))) appel(deleteOfferPlanLine(tid, lineId));
  };

  /* Les trois natures ne sont plus des onglets (2026-09-10), ni même des
     groupes : une seule liste d'offres, dans l'ordre du catalogue, et le total
     de l'entreprise. Trois onglets faisaient chercher une offre avant de la
     lire, et aucun ne donnait le CA entier. */
  const vue = useMemo(() => {
    if (!view) return null;
    const n = (view.years || []).length;
    const groups = (view.groups || []).filter((g) => g.rows.length);
    return { ...view, groups, total: Array.from({ length: n }, (_, i) => round2(groups.reduce((acc, g) => acc + (g.revenue[i] || 0), 0))) };
  }, [view]);
  /* Sans module Finance, aucune ligne de compte de résultat n'existe : le compte
     est alors facultatif, et son absence ne se signale pas comme un oubli. */
  const aucunCompte = (view?.groups || []).every((g) => !g.line_id);
  const years = vue?.years || [];
  const detail = mode === 'detail';
  const showCa = !detail || cols.size === 0 || cols.has('ca');
  const showVol = detail && (cols.size === 0 || cols.has('vol'));
  const showPx = detail && (cols.size === 0 || cols.has('px'));
  /* Le COÛT UNITAIRE (2026-09-12) : le coût des produits vendus se planifie
     ici, par ligne et par année ; un pourcentage du CA pour un montant
     planifié. Vide vaut zéro. */
  const showCost = detail && (cols.size === 0 || cols.has('cost'));
  const nbSaisie = (showVol ? 1 : 0) + (showPx ? 1 : 0) + (showCost ? 1 : 0);      // familles de colonnes saisies affichées
  /* La colonne Offre se règle à la souris sur son entête et se retient
     (2026-09-12) ; l'inducteur tient en peu de place. */
  const COLW = unit === 'chf'
    ? { lbl: largeurLbl, unit: 56, ca: 92, vol: 64, px: 82, cost: 82 }
    : { lbl: largeurLbl, unit: 56, ca: 60, vol: 58, px: 72, cost: 72 };
  const largeur = COLW.lbl + COLW.unit + years.length * ((showCa ? COLW.ca : 0) + (showVol ? COLW.vol : 0) + (showPx ? COLW.px : 0) + (showCost ? COLW.cost : 0));
  const tirerLbl = (e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX; const w0 = largeurLbl;
    const bouge = (ev) => poserLargeurLbl(w0 + ev.clientX - x0);
    const lache = () => { window.removeEventListener('mousemove', bouge); window.removeEventListener('mouseup', lache); };
    window.addEventListener('mousemove', bouge); window.addEventListener('mouseup', lache);
  };
  /* Glisser-déposer des comptes : on dépose un compte sur un autre, il prend
     sa place ; l'ordre appartient à la Trajectoire (document du plan). */
  const reordonne = (ids, moved, targetId) => {
    const list = ids.filter((x) => x !== moved);
    list.splice(list.indexOf(targetId) + (ids.indexOf(moved) < ids.indexOf(targetId) ? 1 : 0), 0, moved);
    return list;
  };
  const onDropLine = async (targetId) => {
    const ids = (view?.groups || []).map((g) => g.line_id).filter(Boolean);   // l'ordre COMPLET : l'autre onglet garde le sien
    const moved = drag?.kind === 'acc' ? drag.id : null;
    setDrag(null);
    if (!moved || moved === targetId || !canWrite || !ids.includes(moved) || !ids.includes(targetId)) return;
    await appel(setOfferPlanLineOrder(tid, reordonne(ids, moved, targetId)));
  };
  /* Les lignes d'une offre se réordonnent entre elles, jamais d'une offre à l'autre. */
  const onDropLigne = async (r, targetId) => {
    const ids = (r.lines || []).map((l) => l.line_id);
    const moved = drag?.kind === 'line' && drag.offer === r.offer_id ? drag.id : null;
    setDrag(null);
    if (!moved || moved === targetId || !canWrite || !ids.includes(targetId)) return;
    await appel(setOfferPlanLinesOrder(tid, r.offer_id, reordonne(ids, moved, targetId)));
  };
  const bascule = (
    <>
      {/* Colonnes en tête : ce réglage disparaît en synthèse, les deux autres ne bougent pas. */}
      {detail && (
        <SegmentedToggle label={p('Colonnes')} multiple value={cols} onChange={setCols}
                         options={[{ value: 'ca', label: p('CA') }, { value: 'vol', label: p('Volume') }, { value: 'px', label: p('Prix') }, { value: 'cost', label: p('Coût') }]} />
      )}
      <SegmentedToggle label={p('Vue')} value={mode} onChange={setMode}
                       options={[{ value: 'detail', label: p('Détail') }, { value: 'synth', label: p('Synthèse') }]} />
      <SegmentedToggle label={p('Grouper')} value={group} onChange={setGroup}
                       options={[{ value: 'offer', label: p('Par offre') }, { value: 'acc', label: p('Par compte') }]} />
    </>
  );

  const enTete = (
    <thead>
      <tr className="op-grouphead">
        <th colSpan={2} />
        {showCa && <th colSpan={years.length} className="op-grp op-grp--ca">{p('CA')} ({unit === 'kchf' ? 'kCHF' : 'CHF'}) · {p('calculé')}</th>}
        {showVol && <th colSpan={years.length} className="op-grp op-grp--vol">{p('Volume')} · {p('saisi')}</th>}
        {showPx && <th colSpan={years.length} className="op-grp op-grp--px">{p('Prix unitaire')} (CHF) · {p('saisi')}</th>}
        {showCost && <th colSpan={years.length} className="op-grp op-grp--cost">{p('Coût unitaire')} (CHF) · {p('saisi')}</th>}
      </tr>
      <tr>
        <th className="op-lbl op-th">{detail || group === 'offer' ? p('Offre') : p('Compte')}
          <span className="op-th__grip" onMouseDown={tirerLbl} title={p('Tirer pour élargir la colonne')} />
        </th>
        <th className="op-unit" />
        {showCa && years.map((y, i) => <th key={`c${y}`} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{y}</th>)}
        {showVol && years.map((y, i) => <th key={`v${y}`} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{y}</th>)}
        {showPx && years.map((y, i) => <th key={`p${y}`} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{y}</th>)}
        {showCost && years.map((y, i) => <th key={`k${y}`} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{y}</th>)}
      </tr>
    </thead>
  );

  /* Les trois groupes de cellules d'une ligne : CA, volume, prix. `edit` = les cellules se tapent
     (une offre planifiée d'un bloc, ou une ligne sous l'offre) ; sinon l'offre lit la somme de ses
     lignes, son prix étant la moyenne pondérée. */
  const cellules = (r, cells, edit, lineId) => (
    <>
      {showCa && cells.map((c, i) => (
        <td key={`c${c.year}`} className={`lk-num${edit ? ' op-ca' : ' op-sum'}${i === 0 ? ' lk-sep' : ''}${edit && c.revenue_typed != null ? ' op-in' : ''}`}>
          {edit
            ? <Cell value={c.revenue_typed != null ? c.revenue_typed : c.revenue} type="number" calc canWrite={canWrite}
                    format={(v) => ca(v)} toDraft={(v) => (v == null ? '' : String(unit === 'kchf' ? Math.round(v / 1000) : Math.round(v)))}
                    placeholder="…" onSave={(v) => write(r.offer_id, c.year, { revenue: v == null ? null : (unit === 'kchf' ? v * 1000 : v) }, lineId)} />
            : ca(c.revenue)}
        </td>
      ))}
      {showVol && cells.map((c, i) => (
        <td key={`v${c.year}`} className={`lk-num${edit ? ' op-in' : ' op-sum'}${i === 0 ? ' lk-sep' : ''}`}>
          {sansInducteur(r)
            ? <span className="op-na">{p('n/a')}</span>
            : !edit
            ? <span className="op-muted">{c.volume == null ? '–' : vol(r, c.volume)}</span>
            : c.revenue_typed != null
              ? <span className="op-muted">–</span>
              : <Cell value={c.volume} type="number" calc canWrite={canWrite} format={(v) => vol(r, v)} placeholder="…" onSave={(v) => write(r.offer_id, c.year, { volume: v }, lineId)} />}
        </td>
      ))}
      {showPx && cells.map((c, i) => (
        <td key={`p${c.year}`} className={`lk-num${edit ? ' op-in' : ' op-sum'}${i === 0 ? ' lk-sep' : ''}`}>
          {sansInducteur(r)
            ? <span className="op-na">{p('n/a')}</span>
            : !edit
            ? <span className="op-muted">{c.price_effective == null ? '–' : nb(c.price_effective)}</span>
            : <Cell value={c.price} type="number" calc canWrite={canWrite} format={(v) => nb(v)}
                      placeholder={c.price_effective == null ? '…' : nb(c.price_effective)}
                      onSave={(v) => write(r.offer_id, c.year, { price: v }, lineId)} />}
          {i === 0 && libellePrix(p, r) && c.price == null ? <span className="op-sub">{libellePrix(p, r)}</span> : null}
        </td>
      ))}
      {showCost && cells.map((c, i) => {
        const montant = r.equation === 'project';   // un montant planifié porte un pourcentage du CA
        const pct = (v) => (v == null ? '' : `${nb(v, 1)} %`);
        return (
          <td key={`k${c.year}`} className={`lk-num${edit ? ' op-in' : ' op-sum'}${i === 0 ? ' lk-sep' : ''}`}>
            {r.equation === 'base_rate'
              ? <span className="op-na">{p('n/a')}</span>
              : !edit
              ? <span className="op-muted">{montant
                  ? (c.cost_pct_effective == null ? '–' : pct(c.cost_pct_effective))
                  : (c.cost_effective == null ? '–' : nb(c.cost_effective))}</span>
              : montant
                ? <Cell value={c.cost_pct} type="number" calc canWrite={canWrite} format={pct} placeholder="0 %"
                        onSave={(v) => write(r.offer_id, c.year, { cost_pct: v }, lineId)} />
                : <Cell value={c.cost} type="number" calc canWrite={canWrite} format={(v) => nb(v)} placeholder="0"
                        onSave={(v) => write(r.offer_id, c.year, { cost: v }, lineId)} />}
          </td>
        );
      })}
    </>
  );

  const ligneOffre = (r) => (
    <React.Fragment key={r.offer_id}>
      <tr className={`op-row${r.has_lines ? ' op-parent' : ''}`}>
        <td className="op-lbl">
          <OfferIcon name={iconOf(r)} size={14} /> <span>{r.name}</span>
          {canWrite && r.equation !== 'base_rate' && (
            <button type="button" className="op-addline" data-tour="trajectory-addline" title={p('Ajouter une ligne')} onClick={() => ajouterLigne(r.offer_id)}>＋ {p('ligne')}</button>
          )}
        </td>
        <td className="op-unit">{libelleVolume(p, r)}</td>
        {cellules(r, r.years, !r.has_lines, null)}
      </tr>
      {(r.lines || []).map((l) => (
        <tr key={l.line_id} className={`op-row op-line${drag?.kind === 'line' && drag.id === l.line_id ? ' is-dragging' : ''}`}
            onDragOver={(e) => { if (drag?.kind === 'line' && drag.offer === r.offer_id) e.preventDefault(); }} onDrop={() => onDropLigne(r, l.line_id)}>
          <td className="op-lbl lk-gutter">
            {canWrite && (
              <span className="lk-grip" draggable title={p('Glisser pour réordonner')}
                    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDrag({ kind: 'line', id: l.line_id, offer: r.offer_id }); }} onDragEnd={() => setDrag(null)}>⋮⋮</span>
            )}
            {/* La croix est DEVANT le libellé : après, un nom long la poussait hors de la colonne. */}
            {canWrite && <button type="button" className="lk-x lk-del" title={p('Supprimer la ligne')} onClick={() => supprimerLigne(l.line_id)}>✕</button>}
            <span className="op-line__lbl"><Cell value={l.label} canWrite={canWrite} onSave={(v) => renommerLigne(l.line_id, v)} /></span>
          </td>
          <td className="op-unit" />
          {cellules(r, l.years, true, l.line_id)}
        </tr>
      ))}
    </React.Fragment>
  );

  /* Les offres dans l'ordre du catalogue, pour les lectures par offre. */
  const offres = useMemo(() => {
    const rows = (vue?.groups || []).flatMap((g) => g.rows);
    const rang = new Map((vue?.offer_order || []).map((id, i) => [id, i]));
    return rows.slice().sort((x, y) => (rang.get(x.offer_id) ?? 1e9) - (rang.get(y.offer_id) ?? 1e9));
  }, [vue]);
  const ligneTotal = (
    <tr className="op-tot">
      <td className="op-lbl" colSpan={2}>{p('Total CA')}</td>
      {showCa && (vue?.total || []).map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{ca(v)}</td>)}
      {nbSaisie > 0 && <td colSpan={years.length * nbSaisie} className="lk-sep" />}
    </tr>
  );
  const ligneCompte = (g) => (
    <tr key={g.line_id || 'none'} className={`op-acc${drag?.kind === 'acc' && drag.id === g.line_id ? ' is-dragging' : ''}`}
        onDragOver={(e) => { if (drag?.kind === 'acc') e.preventDefault(); }} onDrop={() => onDropLine(g.line_id)}>
      <td className="op-lbl lk-gutter" colSpan={2}>
        {canWrite && g.line_id && (
          <span className="lk-grip" draggable title={p('Glisser pour réordonner')}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDrag({ kind: 'acc', id: g.line_id }); }} onDragEnd={() => setDrag(null)}>⋮⋮</span>
        )}
        {g.label || <span className={aucunCompte ? 'op-muted' : 'lk-todo'}>{aucunCompte ? p('Sans compte') : p('Compte à attribuer')}</span>}
      </td>
      {showCa && g.revenue.map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{ca(v)}</td>)}
      {nbSaisie > 0 && <td colSpan={years.length * nbSaisie} className="lk-sep" />}
    </tr>
  );
  /* Synthèse par offre : une ligne par offre, son CA total, sans ses lignes. */
  const ligneOffreSeule = (r) => (
    <tr key={r.offer_id} className="op-row">
      <td className="op-lbl"><OfferIcon name={iconOf(r)} size={14} /> <span>{r.name}</span></td>
      <td className="op-unit">{libelleVolume(p, r)}</td>
      {r.years.map((c, i) => <td key={c.year} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{ca(c.revenue)}</td>)}
    </tr>
  );
  const corps = (
    <tbody>
      {detail && group === 'acc' && (vue?.groups || []).map((g) => (
        <React.Fragment key={g.line_id || 'none'}>{ligneCompte(g)}{g.rows.map(ligneOffre)}</React.Fragment>
      ))}
      {detail && group === 'offer' && offres.map(ligneOffre)}
      {!detail && group === 'acc' && (vue?.groups || []).map((g) => (
        <tr key={g.line_id || 'none'} className="op-row">
          <td className="op-lbl" colSpan={2}>{g.label || <span className={aucunCompte ? 'op-muted' : 'lk-todo'}>{aucunCompte ? p('Sans compte') : p('Compte à attribuer')}</span>}</td>
          {g.revenue.map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{ca(v)}</td>)}
        </tr>
      ))}
      {!detail && group === 'offer' && offres.map(ligneOffreSeule)}
      {ligneTotal}
    </tbody>
  );

  if (!tid) {
    return <PageShell framed title={titre}><p className="op-muted">{p('Choisir un client.')}</p></PageShell>;
  }
  /* La bascule CHF/kCHF vit dans la BANDE DE CONTEXTE, sur la ligne de
     l'exercice (demande d'Olivier, 2026-09-27) ; elle tenait la ligne des
     onglets, sous le titre. Sans onglet, plus de ligne vide. */
  const nav = mesOffres
    ? <Tabs tabs={[{ id: 'offres', label: p('Mes offres') }, { id: 'modeles', label: p('Modèles') }]}
            active={onglet} onChange={setOnglet} />
    : null;
  if (mesOffres) {
    return (
      <PageShell framed title={titre} exportRef={exportRef} unit={pageUnit}>
        {nav}
        <OffersPage only={onglet === 'modeles' ? 'models' : 'offers'}
                    activeTenant={activeTenant} year={year} addToast={addToast} canWrite={canWrite} />
      </PageShell>
    );
  }
  return (
    <PageShell framed title={titre} exportRef={exportRef} unit={pageUnit}>
      {nav}
      <CardShell title={p('Plan par offre')} subtitle={`${years[0] || year} → ${years[years.length - 1] || year + 4}`}
                 viewControls={bascule} flush>
        {vue && vue.groups.length === 0 ? (
          <div className="op-empty">{p('Aucune offre active.')}</div>
        ) : (
          <div className="op-scroll lk-hscroll">
            <table className={`lk-entry-table op-table${detail ? '' : ' op-table--lines'}`} style={{ width: largeur }} data-tour="trajectory-table">
              {/* Colonnes déclarées en pixels : en CHF les montants ont trois chiffres de plus,
                  la table s'élargit et défile dans sa card plutôt que de tronquer. */}
              <colgroup>
                <col style={{ width: COLW.lbl }} /><col style={{ width: COLW.unit }} />
                {showCa && years.map((y) => <col key={`c${y}`} style={{ width: COLW.ca }} />)}
                {showVol && years.map((y) => <col key={`v${y}`} style={{ width: COLW.vol }} />)}
                {showPx && years.map((y) => <col key={`p${y}`} style={{ width: COLW.px }} />)}
                {showCost && years.map((y) => <col key={`k${y}`} style={{ width: COLW.cost }} />)}
              </colgroup>
              {enTete}
              {corps}
            </table>
          </div>
        )}
      </CardShell>
    </PageShell>
  );
}
